import { useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import type { AvailabilitySlot, SlotOption } from '@/api/availability';
import {
  overrideAcknowledgements,
  parseOverrideRefusal,
  parseStaffChange,
  updateReservationDetails,
  type ConflictRefusal,
  type Reservation,
  type StaffChange,
  type UpdateReservationDetailsInput,
} from '@/api/reservations';
import { getService } from '@/api/services';
import { useResource } from '@/hooks/useResource';
import { Icon, resolveTokens } from '@/theme';
import { scrubBrand } from '@/utils/whitelabel';
import { AvailabilitySlotPicker } from '@/features/reservations/AvailabilitySlotPicker';
import { ManualStartTime } from '@/features/reservations/ManualStartTime';
import { focusPicker } from '@/features/reservations/focusPicker';
import { requiredPickerFromError, toBookingErrorMessage } from '@/features/reservations/reservationsError';
import { StaffChangeDialog } from '@/features/reservations/StaffChangeDialog';
import { useKeepAssignment, type KeptOption } from '@/features/reservations/keepAssignment';
import { BayCheckNotice, KeptAssignmentFields } from '@/features/reservations/KeptAssignmentFields';
import { OverrideConflictDialog } from '@/features/reservations/OverrideConflictDialog';
import { hasStaffSideReason, refusedPicker } from '@/features/reservations/refusedPicker';
import { v2ErrorCode } from '@/api/v2Errors';

/** DNKAPI-0636 D-04: the booking changed under this dialog; its seeded fields are stale. */
const STALE_MESSAGE =
  'This booking was changed by someone else and has been reloaded. Close this and open it again.';

interface EditReservationDetailsDialogProps {
  open: boolean;
  reservation: Reservation;
  onClose: () => void;
  /** Success — carries the RETURNED reservation so the caller can patch its row (DNKADM-0257). */
  onDone: (message: string, updated?: Reservation) => void;
  /**
   * DNKAPI-0636 D-04: `409 RESERVATION_CHANGED` — someone else changed the booking first and
   * nothing was written. The host reloads its copy; this dialog blocks Save, because every
   * field it would resend was seeded from the stale row.
   */
  onStale?: () => void;
  /** The location's IANA zone (DNKADM-0390 U2: the manual start time's offset); null → device. */
  timeZone?: string | null;
}

const startDateOf = (iso: string | null): string | null => /^\d{4}-\d{2}-\d{2}/.exec(iso ?? '')?.[0] ?? null;

function mergeOptions(current: SlotOption | null, options: SlotOption[]): SlotOption[] {
  if (current === null || options.some((option) => option.id === current.id)) return options;
  return [current, ...options];
}

/**
 * Pre-confirmation edit dialog. It reuses the reservation availability selector but
 * deliberately owns a separate submit path: no audit reason and PUT `.../details`,
 * so the established reschedule dialog and reschedule-count semantics stay unchanged.
 *
 * **Every admin-override refusal is one dialog (DNKAPI-0542 + DNKAPI-0549, DNKADM-0396).**
 * Moving the proposed time onto the coach's existing booking (or picking a busy coach)
 * comes back `409 STAFF_CONFLICT`; DNKAPI-0549 adds `RESOURCE_CONFLICT` (the bay has no
 * capacity left over the proposed interval) and `STAFF_OUTSIDE_HOURS` / `STAFF_OVER_CAP`
 * (the coach is off shift / past a cap), and **several of those can come back together**.
 * All of them open the {@link OverrideConflictDialog} with every reason the server named:
 * "Change it" returns here with the chooser the reasons point at focused and, when a
 * coach-side reason was among them, the refused pick labelled "(conflict)" — the server's
 * own verdict for THAT coach at THAT start, which clears when either changes; "Book anyway"
 * resends the identical body with every acknowledge flag that refusal named; Cancel changes
 * nothing. This picker is fed by the chosen slot's `staffOptions` (availability at the NEW
 * time), not by `assignment-options`, which answers the reservation's CURRENT interval and
 * would mislabel a moved one.
 *
 * **A coach CHANGE is refused unless acknowledged (DNKAPI-0546, DNKADM-0393 U5).** The
 * twin of the above on the OTHER code path: with no explicit `staffId` (a service whose
 * staff is auto-assigned), a proposed time whose free coach is not the request's current
 * one comes back `409 STAFF_WILL_CHANGE` and opens the {@link StaffChangeDialog} — "Pick
 * another time" returns here with the picker as it was, "Move to <coach>" resends the
 * identical body with `acknowledgeStaffChange: true`, and a `proposedStaff: null`
 * (`MEMBER_SELECTS`) offers only the former. The two refusals are mutually exclusive by
 * construction — 0542 needs a non-null staff override, 0546 a null one — so neither is
 * the other's fallback and they are read by two separate seams.
 *
 * **Only free times, and the coach and bay are kept (DNKADM-0488).** The picker hides booked
 * times (`hideUnavailable`, as Book on behalf) and leaves this request out of the occupancy it
 * measures (`excludeReservationUuid`, DNKAPI-0709), so its own slot stays offered. The
 * request's current coach and bay pin the read and ride on the save explicitly — see
 * `keepAssignment` for Change coach / Change bay and the "Bay N is taken at this time" choice.
 * A dimension the request has no current assignment for keeps the post-slot chooser below.
 */
export function EditReservationDetailsDialog({
  open,
  reservation,
  onClose,
  onDone,
  onStale,
  timeZone = null,
}: EditReservationDetailsDialogProps) {
  const ids = useId();
  const theme = useTheme();
  const t = resolveTokens(theme);
  const titleId = `${ids}-title`;
  const [start, setStart] = useState(reservation.start ?? '');
  // The manual time's date when no picker owns one (DNKADM-0390 U2): the request's own date.
  const [manualDate, setManualDate] = useState<string | null>(startDateOf(reservation.start));
  const [slot, setSlot] = useState<AvailabilitySlot | null>(null);
  const [staffId, setStaffId] = useState(reservation.assignedStaffId ?? '');
  const [resourceId, setResourceId] = useState(reservation.assignedResourceId ?? '');
  const [missingPicker, setMissingPicker] = useState<'staff' | 'resource' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [stale, setStale] = useState(false);
  // An admin-override refusal (DNKADM-0396): the server's answer — every reason it named —
  // plus the EXACT body it refused, so "Book anyway" resends that body with the flags that
  // refusal named, not a re-derivation from state.
  const [conflict, setConflict] = useState<{
    view: ConflictRefusal;
    input: UpdateReservationDetailsInput;
  } | null>(null);
  // A `409 STAFF_WILL_CHANGE` (DNKADM-0393 U5) — the same shape on the other code path:
  // the server's answer plus the EXACT body it refused, resent verbatim on "Move to…".
  const [staffChange, setStaffChange] = useState<{
    view: StaffChange;
    input: UpdateReservationDetailsInput;
  } | null>(null);
  // The pick the server refused (coach + start) — labelled "(conflict)" in the chooser
  // until either changes. Read from the refused request, never computed client-side.
  const [refusedPick, setRefusedPick] = useState<{ staffId: string; start: string } | null>(null);
  // "Change it" → focus the chooser the server's reasons point at, once the dialog is gone.
  const staffFieldRef = useRef<HTMLDivElement>(null);
  const resourceFieldRef = useRef<HTMLDivElement>(null);
  const [focusRequest, setFocusRequest] = useState<{ n: number; field: 'staff' | 'resource' }>({
    n: 0,
    field: 'staff',
  });
  useEffect(() => {
    if (focusRequest.n === 0) return;
    focusPicker(
      focusRequest.field === 'resource' ? resourceFieldRef.current : staffFieldRef.current,
    );
  }, [focusRequest]);

  const service = useResource(
    () => reservation.serviceId ? getService(reservation.serviceId) : Promise.resolve(null),
    [reservation.serviceId],
  );
  const needsStaff = service.data?.staffAssignment === 'MEMBER_SELECTS';
  const needsResource = service.data?.resourceAssignment === 'MEMBER_SELECTS';
  const keep = useKeepAssignment({
    reservation,
    staffMode: service.data?.staffAssignment ?? null,
    resourceMode: service.data?.resourceAssignment ?? null,
    // A new coach or bay re-reads the times; the picked one may not be free there.
    onPinsChange: () => {
      setSlot(null);
      setStart(reservation.start ?? '');
    },
  });
  // The post-slot choosers serve only a dimension with nothing to keep (DNKADM-0488).
  const legacyStaff = needsStaff && !keep.coach.active;
  const legacyResource = needsResource && !keep.bay.active;
  // Times wait for the service's modes when there is a coach or bay to pin, so the first read
  // is already pinned.
  const timesReady =
    !(keep.coach.current !== null || keep.bay.current !== null) || service.status !== 'loading';
  const canPickSlots =
    reservation.serviceId !== null &&
    reservation.variationId !== null &&
    reservation.locationId !== null;

  const currentStaff = reservation.assignedStaffId
    ? { id: reservation.assignedStaffId, name: reservation.assignedStaffName }
    : null;
  const currentResource = reservation.assignedResourceId
    ? { id: reservation.assignedResourceId, name: reservation.assignedResourceName }
    : null;
  const staffOptions = mergeOptions(currentStaff, slot?.staffOptions ?? []);
  const resourceOptions = mergeOptions(currentResource, slot?.resourceOptions ?? []);

  function close() {
    if (!submitting) onClose();
  }

  function chooseSlot(next: AvailabilitySlot) {
    setSlot(next);
    setStart(next.start);
    setMissingPicker(null);
    keep.onSlot(next);
    if (legacyStaff && !next.staffOptions.some((option) => option.id === staffId)) setStaffId('');
    if (legacyResource && !next.resourceOptions.some((option) => option.id === resourceId)) setResourceId('');
  }

  async function save() {
    setError(null);
    setMissingPicker(null);
    if (!reservation.reservationUuid) {
      setError('This request can’t be edited (missing id).');
      return;
    }
    if (!start.trim()) {
      setError('Pick a proposed start time.');
      return;
    }
    if (service.status !== 'ready' || service.data === null) {
      setError('The service assignment rules could not be loaded. Try again.');
      return;
    }
    if (legacyStaff && !staffId) {
      setMissingPicker('staff');
      setError('Choose a staff member for this request.');
      return;
    }
    if (legacyResource && !resourceId) {
      setMissingPicker('resource');
      setError('Choose a resource for this request.');
      return;
    }
    if (keep.bay.active && keep.bay.resolved === null) {
      setError('Pick a free bay for this time.');
      return;
    }

    await send(reservation.reservationUuid, {
      start: start.trim(),
      ...(legacyStaff ? { staffId } : {}),
      ...(legacyResource ? { resourceId } : {}),
      // The kept (or changed) coach and bay, always explicit so the server never re-picks them.
      ...keep.writeIds,
    });
  }

  /** The one write path — the first attempt and the acknowledged resend share it. */
  async function send(uuid: string, input: UpdateReservationDetailsInput) {
    setError(null);
    setSubmitting(true);
    try {
      const updated = await updateReservationDetails(uuid, input);
      onDone('Proposed details updated.', updated);
    } catch (err) {
      setSubmitting(false);
      const refusal = parseOverrideRefusal(err);
      if (refusal) {
        setConflict({ view: refusal, input });
        // The "(conflict)" annotation is a claim ABOUT THE COACH, so it is set only when a
        // coach-side reason was among those the server named — a bay-only refusal must not
        // label the coach as in conflict (DNKADM-0396).
        if (input.staffId !== undefined && hasStaffSideReason(refusal)) {
          setRefusedPick({ staffId: input.staffId, start: input.start });
        }
        return;
      }
      if (v2ErrorCode(err) === 'RESERVATION_CHANGED') {
        // DNKAPI-0636 D-04: a resend would carry the stale row's start/coach/bay and revert
        // the other write, so Save stays off until the dialog is reopened on fresh data.
        setStale(true);
        setError(STALE_MESSAGE);
        onStale?.();
        return;
      }
      const change = parseStaffChange(err);
      if (change) {
        setStaffChange({ view: change, input });
        return;
      }
      const required = requiredPickerFromError(err);
      if (required) setMissingPicker(required);
      setError(toBookingErrorMessage(err));
    }
  }

  /** "(conflict)" on the exact coach + start the server refused; plain otherwise. */
  function staffOptionLabel(option: SlotOption | KeptOption): string {
    const name = scrubBrand(option.name ?? option.id) ?? option.id;
    return refusedPick !== null && refusedPick.staffId === option.id && refusedPick.start === start.trim()
      ? `${name} (conflict)`
      : name;
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Avatar variant="rounded" sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}>
          <Icon name="edit_calendar" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Edit proposed details</Typography>
          <Typography variant="body2">{scrubBrand(reservation.serviceName ?? 'Reservation')}</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Current proposed start: {start || '—'}
        </Typography>

        <KeptAssignmentFields
          keep={keep}
          disabled={submitting}
          coachFieldRef={staffFieldRef}
          bayFieldRef={resourceFieldRef}
          coachLabel={staffOptionLabel}
        />

        {canPickSlots && reservation.serviceId && reservation.variationId && reservation.locationId ? (
          timesReady && (
            // The request's member's availability (DNKADM-0390 U1): the admin route, keyed by the
            // reservation's contact. Times that fail to load fall back to the picker's manual time.
            <AvailabilitySlotPicker
              contactId={reservation.contactId}
              serviceId={reservation.serviceId}
              variationId={reservation.variationId}
              locationId={reservation.locationId}
              {...keep.pickerProps}
              hideUnavailable
              timeZone={timeZone}
              selectedStart={start || null}
              onSelect={chooseSlot}
              onManualStart={(iso) => {
                setStart(iso ?? '');
                setSlot(null);
                keep.onSlot(null);
              }}
            />
          )
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 1 }}>
              Available times can’t be loaded for this request — pick a start time manually.
            </Alert>
            {/* No picker owns a date here (a degraded request), so the date is editable too. */}
            <ManualStartTime
              date={manualDate}
              onDateChange={setManualDate}
              timeZone={timeZone}
              value={start || null}
              onChange={(iso) => {
                setStart(iso ?? '');
                setSlot(null);
              }}
              disabled={submitting}
            />
          </>
        )}

        <BayCheckNotice keep={keep} disabled={submitting} />

        {service.status === 'loading' && (
          <Typography variant="body2" sx={{ mt: 1.5 }}>Loading assignment rules…</Typography>
        )}
        {service.status === 'error' && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>The service assignment rules couldn’t be loaded.</Alert>
        )}

        <Stack sx={{ gap: 1.5, mt: 1.5 }}>
          {legacyStaff && (
            <TextField
              ref={staffFieldRef}
              select
              label="Staff"
              required
              size="small"
              value={staffId}
              onChange={(event) => setStaffId(event.target.value)}
              error={missingPicker === 'staff'}
              helperText={missingPicker === 'staff' ? 'Choose a staff member.' : ' '}
              disabled={submitting}
            >
              {staffOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {staffOptionLabel(option)}
                </MenuItem>
              ))}
            </TextField>
          )}
          {legacyResource && (
            <TextField
              ref={resourceFieldRef}
              select
              label="Resource"
              required
              size="small"
              value={resourceId}
              onChange={(event) => setResourceId(event.target.value)}
              error={missingPicker === 'resource'}
              helperText={missingPicker === 'resource' ? 'Choose a resource.' : ' '}
              disabled={submitting}
            >
              {resourceOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {scrubBrand(option.name ?? option.id)}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        {error && <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={
            submitting ||
            stale ||
            !start.trim() ||
            service.status !== 'ready' ||
            (keep.bay.active && keep.bay.resolved === null)
          }
        >
          {submitting ? 'Saving…' : 'Save details'}
        </Button>
      </DialogActions>

      {/* The admin-override refusal (DNKADM-0396) — a dialog over this one, never a toast.
          Mounted only while a refusal is pending so its acknowledgement tick starts clean
          every time. */}
      {conflict && reservation.reservationUuid && (
        <OverrideConflictDialog
          open
          refusal={conflict.view}
          submitting={submitting}
          onCancel={() => setConflict(null)}
          onChangeIt={() => {
            const field = refusedPicker(conflict.view);
            setConflict(null);
            // A kept coach/bay shows its list first, so the focus lands on a chooser.
            if (field === 'staff' && keep.coach.active) keep.coach.startChange();
            if (field === 'resource' && keep.bay.active) keep.bay.startChange();
            setFocusRequest((prev) => ({ n: prev.n + 1, field }));
          }}
          onBookAnyway={() => {
            const { input, view } = conflict;
            const uuid = reservation.reservationUuid;
            setConflict(null);
            // Exactly the flags THIS refusal named — never one for a decision the admin
            // was not shown.
            if (uuid) void send(uuid, { ...input, ...overrideAcknowledgements(view) });
          }}
        />
      )}

      {/* The coach-change warning (DNKADM-0393 U5) — the OTHER 409 this save can get,
          read by its own seam and never a fallback for the conflict above. */}
      {staffChange && reservation.reservationUuid && (
        <StaffChangeDialog
          open
          change={staffChange.view}
          action="edit"
          submitting={submitting}
          onPickAnotherTime={() => setStaffChange(null)}
          onMoveToProposed={() => {
            const { input } = staffChange;
            const uuid = reservation.reservationUuid;
            setStaffChange(null);
            if (uuid) void send(uuid, { ...input, acknowledgeStaffChange: true });
          }}
        />
      )}
    </Dialog>
  );
}
